import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { apiBaseUrl } from '../../util/api';

// ================ Action types ================ //

export const FETCH_PROVIDERS_REQUEST = 'app/AdminPage/FETCH_PROVIDERS_REQUEST';
export const FETCH_PROVIDERS_SUCCESS = 'app/AdminPage/FETCH_PROVIDERS_SUCCESS';
export const FETCH_PROVIDERS_ERROR = 'app/AdminPage/FETCH_PROVIDERS_ERROR';

export const FETCH_PROVIDER_COUNTS_REQUEST = 'app/AdminPage/FETCH_PROVIDER_COUNTS_REQUEST';
export const FETCH_PROVIDER_COUNTS_SUCCESS = 'app/AdminPage/FETCH_PROVIDER_COUNTS_SUCCESS';
export const FETCH_PROVIDER_COUNTS_ERROR = 'app/AdminPage/FETCH_PROVIDER_COUNTS_ERROR';

export const FETCH_INACTIVE_EXPORT_META_REQUEST = 'app/AdminPage/FETCH_INACTIVE_EXPORT_META_REQUEST';
export const FETCH_INACTIVE_EXPORT_META_SUCCESS = 'app/AdminPage/FETCH_INACTIVE_EXPORT_META_SUCCESS';
export const FETCH_INACTIVE_EXPORT_META_ERROR = 'app/AdminPage/FETCH_INACTIVE_EXPORT_META_ERROR';

export const FETCH_PROVIDER_USERS_EXPORT_META_REQUEST =
  'app/AdminPage/FETCH_PROVIDER_USERS_EXPORT_META_REQUEST';
export const FETCH_PROVIDER_USERS_EXPORT_META_SUCCESS =
  'app/AdminPage/FETCH_PROVIDER_USERS_EXPORT_META_SUCCESS';
export const FETCH_PROVIDER_USERS_EXPORT_META_ERROR =
  'app/AdminPage/FETCH_PROVIDER_USERS_EXPORT_META_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchProvidersInProgress: false,
  fetchProvidersError: null,
  providerUserRefs: [],
  providersMeta: {
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 0,
    accountState: 'all',
    q: null,
  },
  providerCounts: {
    total: 0,
    active: 0,
    inactive: 0,
    unknown: 0,
    computedAt: null,
    source: null,
  },
  fetchProviderCountsInProgress: false,
  fetchProviderCountsError: null,
  inactiveExportMeta: {
    generatedAt: null,
    rowCount: 0,
    available: false,
  },
  fetchInactiveExportMetaInProgress: false,
  fetchInactiveExportMetaError: null,
  providerUsersExportMeta: {
    generatedAt: null,
    rowCount: 0,
    available: false,
  },
  fetchProviderUsersExportMetaInProgress: false,
  fetchProviderUsersExportMetaError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_PROVIDERS_REQUEST:
      return {
        ...state,
        fetchProvidersInProgress: true,
        fetchProvidersError: null,
      };
    case FETCH_PROVIDERS_SUCCESS: {
      const users = payload?.data?.data || [];
      const meta = payload?.data?.meta || initialState.providersMeta;
      return {
        ...state,
        fetchProvidersInProgress: false,
        providerUserRefs: users.map(user => user.id),
        providersMeta: {
          currentPage: meta.currentPage || 1,
          itemsPerPage: meta.itemsPerPage || 10,
          totalItems: meta.totalItems || 0,
          totalPages: meta.totalPages || 0,
          accountState: meta.accountState || 'all',
          q: meta.q || null,
          ignoredNonEmailQuery: !!meta.ignoredNonEmailQuery,
        },
      };
    }
    case FETCH_PROVIDERS_ERROR:
      return {
        ...state,
        fetchProvidersInProgress: false,
        fetchProvidersError: payload,
      };
    case FETCH_PROVIDER_COUNTS_REQUEST:
      return {
        ...state,
        fetchProviderCountsInProgress: true,
        fetchProviderCountsError: null,
      };
    case FETCH_PROVIDER_COUNTS_SUCCESS: {
      const data = payload?.data || {};
      return {
        ...state,
        fetchProviderCountsInProgress: false,
        providerCounts: {
          total: data.total || 0,
          active: data.active || 0,
          inactive: data.inactive || 0,
          unknown: data.unknown || 0,
          computedAt: data.computedAt || null,
          source: data.source || null,
        },
      };
    }
    case FETCH_PROVIDER_COUNTS_ERROR:
      return {
        ...state,
        fetchProviderCountsInProgress: false,
        fetchProviderCountsError: payload,
      };
    case FETCH_INACTIVE_EXPORT_META_REQUEST:
      return {
        ...state,
        fetchInactiveExportMetaInProgress: true,
        fetchInactiveExportMetaError: null,
      };
    case FETCH_INACTIVE_EXPORT_META_SUCCESS: {
      const data = payload?.data || {};
      return {
        ...state,
        fetchInactiveExportMetaInProgress: false,
        inactiveExportMeta: {
          generatedAt: data.generatedAt || null,
          rowCount: data.rowCount || 0,
          available: !!data.available,
        },
      };
    }
    case FETCH_INACTIVE_EXPORT_META_ERROR:
      return {
        ...state,
        fetchInactiveExportMetaInProgress: false,
        fetchInactiveExportMetaError: payload,
      };
    case FETCH_PROVIDER_USERS_EXPORT_META_REQUEST:
      return {
        ...state,
        fetchProviderUsersExportMetaInProgress: true,
        fetchProviderUsersExportMetaError: null,
      };
    case FETCH_PROVIDER_USERS_EXPORT_META_SUCCESS: {
      const data = payload?.data || {};
      return {
        ...state,
        fetchProviderUsersExportMetaInProgress: false,
        providerUsersExportMeta: {
          generatedAt: data.generatedAt || null,
          rowCount: data.rowCount || 0,
          available: !!data.available,
        },
      };
    }
    case FETCH_PROVIDER_USERS_EXPORT_META_ERROR:
      return {
        ...state,
        fetchProviderUsersExportMetaInProgress: false,
        fetchProviderUsersExportMetaError: payload,
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchProvidersRequest = () => ({ type: FETCH_PROVIDERS_REQUEST });
export const fetchProvidersSuccess = response => ({
  type: FETCH_PROVIDERS_SUCCESS,
  payload: response,
});
export const fetchProvidersError = error => ({
  type: FETCH_PROVIDERS_ERROR,
  error: true,
  payload: error,
});

export const fetchProviderCountsRequest = () => ({ type: FETCH_PROVIDER_COUNTS_REQUEST });
export const fetchProviderCountsSuccess = response => ({
  type: FETCH_PROVIDER_COUNTS_SUCCESS,
  payload: response,
});
export const fetchProviderCountsError = error => ({
  type: FETCH_PROVIDER_COUNTS_ERROR,
  error: true,
  payload: error,
});

export const fetchInactiveExportMetaRequest = () => ({
  type: FETCH_INACTIVE_EXPORT_META_REQUEST,
});
export const fetchInactiveExportMetaSuccess = response => ({
  type: FETCH_INACTIVE_EXPORT_META_SUCCESS,
  payload: response,
});
export const fetchInactiveExportMetaError = error => ({
  type: FETCH_INACTIVE_EXPORT_META_ERROR,
  error: true,
  payload: error,
});

export const fetchProviderUsersExportMetaRequest = () => ({
  type: FETCH_PROVIDER_USERS_EXPORT_META_REQUEST,
});
export const fetchProviderUsersExportMetaSuccess = response => ({
  type: FETCH_PROVIDER_USERS_EXPORT_META_SUCCESS,
  payload: response,
});
export const fetchProviderUsersExportMetaError = error => ({
  type: FETCH_PROVIDER_USERS_EXPORT_META_ERROR,
  error: true,
  payload: error,
});

// ================ Thunks ================ //

/**
 * @param {number} [page]
 * @param {number} [limit]
 * @param {{ q?: string, accountState?: string }} [filters]
 */
export const fetchProviders = (page = 1, limit = 10, filters = {}) => (dispatch, getState, sdk) => {
  dispatch(fetchProvidersRequest());

  const url = new URL(`${apiBaseUrl()}/api/admin-providers`);
  url.searchParams.set('page', String(page));
  url.searchParams.set('limit', String(limit));

  const q = typeof filters.q === 'string' ? filters.q.trim() : '';
  if (q) {
    url.searchParams.set('q', q);
  }

  const accountState = filters.accountState || 'all';
  if (accountState && accountState !== 'all') {
    url.searchParams.set('accountState', accountState);
  }

  return fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchProvidersSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchProvidersError(storableError(e)));
      throw e;
    });
};

export const fetchProviderCounts = () => (dispatch, getState, sdk) => {
  dispatch(fetchProviderCountsRequest());

  const url = `${apiBaseUrl()}/api/admin-providers/counts`;

  return fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(response => {
      dispatch(fetchProviderCountsSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchProviderCountsError(storableError(e)));
      throw e;
    });
};

export const fetchInactiveExportMeta = () => (dispatch, getState, sdk) => {
  dispatch(fetchInactiveExportMetaRequest());

  const url = `${apiBaseUrl()}/api/admin-providers/inactive-export/meta`;

  return fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(response => {
      dispatch(fetchInactiveExportMetaSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchInactiveExportMetaError(storableError(e)));
      throw e;
    });
};

/**
 * Trigger browser download of the deposited inactive CSV (super-user cookie auth).
 * @returns {Promise<void>}
 */
export const downloadInactiveProvidersCsv = () => () => {
  const url = `${apiBaseUrl()}/api/admin-providers/inactive-export`;
  return fetch(url, {
    method: 'GET',
    credentials: 'include',
  }).then(async response => {
    if (!response.ok) {
      const details = await response.text();
      throw new Error(details || `HTTP error! status: ${response.status}`);
    }
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = 'admin-inactive-providers.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(objectUrl);
  });
};

export const fetchProviderUsersExportMeta = () => (dispatch, getState, sdk) => {
  dispatch(fetchProviderUsersExportMetaRequest());

  const url = `${apiBaseUrl()}/api/admin-providers/provider-users-export/meta`;

  return fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(response => {
      dispatch(fetchProviderUsersExportMetaSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchProviderUsersExportMetaError(storableError(e)));
      throw e;
    });
};

/**
 * Trigger browser download of the nightly all-providers CSV (super-user cookie auth).
 * The server never builds this file on request; a missing file is a 404.
 * @returns {Promise<void>}
 */
export const downloadProviderUsersCsv = () => () => {
  const url = `${apiBaseUrl()}/api/admin-providers/provider-users-export`;
  return fetch(url, {
    method: 'GET',
    credentials: 'include',
  }).then(async response => {
    if (!response.ok) {
      const text = await response.text();
      let details = text;
      try {
        const body = JSON.parse(text);
        details = body.details || body.error || text;
      } catch (_e) {
        // not JSON; show as-is
      }
      throw new Error(details || `HTTP error! status: ${response.status}`);
    }
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = 'provider-users.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(objectUrl);
  });
};

// ================ Selectors ================ //

export const getProviders = state => {
  const { providerUserRefs } = state.AdminPage;
  const { entities } = state.marketplaceData;

  const providers = providerUserRefs
    .map(ref => {
      const provider = entities.user && entities.user[ref.uuid];
      return provider;
    })
    .filter(Boolean);

  return providers;
};

export const getFetchProvidersInProgress = state => state.AdminPage.fetchProvidersInProgress;
export const getFetchProvidersError = state => state.AdminPage.fetchProvidersError;
export const getProvidersMeta = state => state.AdminPage.providersMeta || initialState.providersMeta;
export const getProviderCounts = state =>
  state.AdminPage.providerCounts || initialState.providerCounts;
export const getFetchProviderCountsInProgress = state =>
  state.AdminPage.fetchProviderCountsInProgress;
export const getFetchProviderCountsError = state => state.AdminPage.fetchProviderCountsError;
export const getInactiveExportMeta = state =>
  state.AdminPage.inactiveExportMeta || initialState.inactiveExportMeta;
export const getProviderUsersExportMeta = state =>
  state.AdminPage.providerUsersExportMeta || initialState.providerUsersExportMeta;

// ================ Page loadData ================ //

export const loadData = () => (dispatch, getState, sdk) => {
  return Promise.resolve();
};
